import { useTranslation } from "react-i18next"
import WhiteSpace from "../../components/Whitespace"
import PageTitle from "../../components/PageTitle"
import styled from "styled-components"
import { useEffect, useState, useRef} from "react"
import Comm from "../../Comm"
import ChargerType from "../../types/ChargerType"
import Dropdown from "../../components/Dropdown"
import ButtonWithIcon from "../../components/ButtonWithIcon"
import Table from "../../modules/Table"
import { TableCellTypes } from "../../enums/TableCellTypes"
import Modal from "../../components/Modal"
import Icon from "../../Icon"
import Button from "../../components/Button"
import InputWithLabel from "../../components/InputWithLabel"
import Switch from "../../components/Switch"
import Select from "../../components/Select"
import LoaderDots from "../../components/LoaderDots"
import InputWithIcon from "../../components/InputWithIcon"
import { child, get, getDatabase, ref } from "firebase/database"
import fbConf from "../../FirebaseConf"
import readXlsxFile from 'read-excel-file'
import writeXlsxFile from "write-excel-file"
import ExportButton from "../../modules/ExportButton"
import UserType from "../../types/UserType"
import RfidType from "../../types/RfidType"
import { useNavigate } from "react-router-dom"
import { useConfirm } from "../../ConfirmProvider"
import normalizeString from "../../utils/normalizeString"
import { useNotification } from "../../modules/Notification"

const ToolbarContaier = styled.div`
  position: absolute;
  right: 0px;
  display: flex;

  /* See ErrorLogs: an absolute toolbar cannot wrap, so below this it lands on top of the table's
     search row. Back into flow, wrapping. The bound is 1400 rather than the shell breakpoint
     because at 1280 the five buttons still overlap the search field. */
  @media (max-width: 1400px) {
    position: static;
    right: auto;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;

    /* The row's explicit spacer divs survive the wrap and indent every line after the first by a
       different amount. The gap above already does the spacing. */
    > div:empty {
      display: none;
    }
  }
`

const RefreshTimerLabel = styled.p`
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 4px;
  text-align: center;
`

const ModalContainer = styled.div`
  padding-left: 88px;
  padding-right: 88px;
  padding-top: 65px;
  padding-bottom: 65px;
`

const ModalCloseButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  right: 26px;
`

const SwitchLabel = styled.p`
  font-size: 12px;
  font-weight: 700;
  margin-top: 8px;
  color: ${props => props.theme.colors.gray_900};
`

const EmptyTableLabel = styled.p`
  width: 100%;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.gray_900};
  margin-top: 48px;
  position: absolute;
`

const CopyRfidsModalChargerLabel = styled.p<{ $isOffline?: boolean }>`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.$isOffline ? props.theme.colors.gray_600 : props.theme.colors.gray_900};
`

const ModalButtonContainer = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  width: calc(100% - 10px);
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
`

const ModalButton = styled.p<{ $hasBg?: boolean }>`
  font-weight: 600;
  font-size: 15px;
  color: ${props => props.$hasBg ? props.theme.colors.white : props.theme.colors.gray_500};
  padding-top: 8px;
  padding-bottom: 8px;
  margin: 16px;
  width: 20%;
  text-align: center;
  border-radius: 12px;
  float: right;
  background-color: ${props => props.$hasBg ? props.theme.colors.dark : 'none'};
  cursor: pointer;
`

const FileDropContainer = styled.div`
  width: 100%;
  border-radius: 16px;
  background-color: ${props => props.theme.colors.primaryLight};
  border: 1px dashed ${props => props.theme.colors.primary};
  cursor: pointer;
`

const FileDropLabel = styled.p`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  width: 100%;
  text-align: center;
  margin: 0px;
`

// The v2 access endpoints answer with a GeneralSystemResponse ({ error, message, data }) whose message
// is already in the user's language, where v1 only had the raw VOLTIE_ERROR_x code. The message repeats
// that code as a prefix ("VOLTIE_ERROR_3 - Felhasznalo nem talalhato"), which is dropped here (VLT-2869).
async function readAccessErrorMessage(response: Response): Promise<string | null> {
  try {
    const body = await response.json()
    if (typeof body?.message !== 'string' || !body.message.trim()) return null
    return body.message.replace(/^VOLTIE_[A-Z0-9_]+\s*-\s*/, '').trim() || body.message.trim()
  } catch {
    return null
  }
}

type BulkInviteStatus = { state: 'success' | 'unknownEmail' | 'error', message?: string }

// The levels this page can give; 11 is the Restricted User of VLT-3034.
type InviteAccessLevel = 2 | 3 | 9 | 10 | 11
const INVITE_ACCESS_LEVELS: InviteAccessLevel[] = [2, 3, 9, 10, 11]
const ROLE_SELECT_OPTIONS = ["User", "Admin", "RFID Only", "Apartment User", "Restricted User"]
const roleLabelToAccessLevel = (label: string): InviteAccessLevel => { const l = label.trim().toLowerCase(); return l === "admin" ? 3 : l === "rfid only" ? 9 : l === "apartment user" ? 10 : l === "restricted user" ? 11 : 2 }
const accessLevelToRoleLabel: { [key: number]: string } = { 2: "User", 3: "Admin", 9: "RFID Only", 10: "Apartment User", 11: "Restricted User" }

export default function UsersRfid({ charger, isAdmin }: { charger: ChargerType, isAdmin: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { addNotification } = useNotification()

  // Hidden file inputs: the File System Access API is absent on Safari iOS / Chrome Android.
  const rfidFileInputRef = useRef<HTMLInputElement>(null)
  const inviteFileInputRef = useRef<HTMLInputElement>(null)
  const [users, setUsers] = useState<UserType[] | null>()
  const [rfids, setRfids] = useState<RfidType[] | null>()
  const [lastUsersRefresh, setLastUsersRefresh] = useState<string>('')
  const [lastRfidsRefresh, setLastRfidsRefresh] = useState<string>('')

  //const [isEditingUser, setIsEditingUser] = useState<UserType | null>()

  const [isEditingRfid, setIsEditingRfid] = useState<boolean>(false)
  const [isAddingRfid, setIsAddingRfid] = useState<boolean>(false)
  const [rfidIdInput, setRfidIdInput] = useState<string>()
  const [rfidNameInput, setRfidNameInput] = useState<string>()
  const [rfidEnabledInput, setRfidEnabledInput] = useState<boolean>()
  const [rfidEmailInput, setRfidEmailInput] = useState<string>()

  const [isAddingUser, setIsAddingUser] = useState<boolean>(false)
  const [userEmailInput, setUserEmailInput] = useState<string>()
  const [userNameInput, setUserNameInput] = useState<string>()
  const [userAccessLevelInput, setUserAccessLevelInput] = useState<InviteAccessLevel>(2)
  const [isAddingUserError, setIsAddingUserError] = useState<string | null>(null)
  const [isAddingUserLoading, setIsAddingUserLoading] = useState<boolean>(false)

  const [changingRoleUser, setChangingRoleUser] = useState<UserType | null>(null)
  const [changeRoleAccessLevelInput, setChangeRoleAccessLevelInput] = useState<number>(2)
  const [isChangingRoleError, setIsChangingRoleError] = useState<string | null>(null)
  const [isChangingRoleLoading, setIsChangingRoleLoading] = useState<boolean>(false)

  const [isCopyingRfid, setIsCopyingRfid] = useState<boolean>(false)
  const [chargersToCopyRfidsTo, setChargersToCopyRfidsTo] = useState<ChargerType[]>()
  const [isCopyingRfidsInProcess, setIsCopyingRfidsInProcess] = useState<boolean>(false)
  const [chargersThatRfidsAreCopiedTo, setChargersThatRfidsAreCopiedTo] = useState<string[]>()
  const [chargersToShowAfterSearch, setChargersToShowAfterSearch] = useState<ChargerType[]>()
  const [chargers, setChargers] = useState<ChargerType[]>([])

  const [isUploadingRfidsFromFile, setIsUploadingRfidsFromFile] = useState<boolean>(false)
  const [uploadingRfidsList, setUploadingRfidsList] = useState<RfidType[] | null>()

  const [isTransferingOwnership, setIsTransferingOwnership] = useState<boolean>(false)
  const [transferOwenrshipEmailInput, setTransferOwenrshipEmailInput] = useState<string>()
  const [isTransferingOwnershipError, setIsTransferingOwnershipError] = useState<string | null>(null)
  const [isTransferingOwnershipLoading, setIsTransferingOwnershipLoading] = useState<boolean>(false)

  // Bulk user invite from an xls file (VLT-2129)
  const [isBulkInvitingUsers, setIsBulkInvitingUsers] = useState<boolean>(false)
  // accessLevel is null where the file's role was not recognised; those rows are not invited.
  const [bulkInviteUsersList, setBulkInviteUsersList] = useState<{ name: string, email: string, role: string, accessLevel: InviteAccessLevel | null }[] | null>(null)
  const [bulkInviteChargers, setBulkInviteChargers] = useState<ChargerType[]>([])
  const [bulkInviteStatuses, setBulkInviteStatuses] = useState<{ [email: string]: BulkInviteStatus }>({})
  const [isBulkInviteInProcess, setIsBulkInviteInProcess] = useState<boolean>(false)



  useEffect(() => {
    async function fetchUsersAndRfids() {
      await fetchUsers()
      await fetchRfids()
    }
    fetchUsersAndRfids()
  }, [])

  async function fetchChargers() {
    let result
    if (isAdmin) {
      const response = await Comm('/api-charger/chargers', {})
      if (response.status != 200) { return }
      result = await response.json()

    } else {
      const response = await Comm('/api-charger/user/chargers', {})
      if (response.status != 200) { return }
      result = await response.json()

      if (result.length > 0) {
        // Get users chargers from firebase to get the firebase charger name
        try {
          const firebaseChargerNamesResponse = await get(child(ref(getDatabase(fbConf)), `voltie/chargerusers/${result[0].userId}/chargers`))
          const firebaseChargerNames = await firebaseChargerNamesResponse.val()
          for (const charger of result) {
            charger.localName = firebaseChargerNames[charger.chargerId]?.name || charger.localName
          }
        } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
      }
    }

    // Fetch and set the list of online chargers
    const onlineChargersResponse = await Comm('/api-websocket/online', {})
    const onlineChargers = await onlineChargersResponse.json()
    for (const charger of result) {
      charger.online = onlineChargers.includes(charger.chargerId)
    }

    setChargers(result)
    setChargersToShowAfterSearch(result)
  }

  async function fetchUsers() {
    try {
      const response = await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 46 })
      })
      const result = await response.json()
      setUsers(result.users)
      setLastUsersRefresh(new Date().toString().split(' ')[4])
    } catch (e) { console.log(e, 'Charger probably offline') }
  }

  async function fetchRfids() {
    try {
      const response = await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 23 })
      })
      const result = await response.json()
      setRfids(result.rfid_list)
      setLastRfidsRefresh(new Date().toString().split(' ')[4])
    } catch (e) { console.log(e, 'Charger probably offline') }
  }

  function handleRefreshUsers() {
    setUsers(null)
    fetchUsers()
  }

  function handleRefreshRfids() {
    setRfids(null)
    fetchRfids()
  }

  function startEditRfid(rfid: RfidType) {
    setRfidIdInput(rfid.id)
    setRfidNameInput(rfid.name)
    setRfidEmailInput(rfid.email)
    setRfidEnabledInput(rfid.enabled)
    setIsEditingRfid(true)
  }

  async function saveEditRfid() {
    await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 25, "rfid_tag": { "id": rfidIdInput, "name": rfidNameInput, "enabled": rfidEnabledInput, "email": rfidEmailInput } })
    })
    setRfidIdInput('')
    setRfidNameInput('')
    setRfidEmailInput('')
    setRfidEnabledInput(true)
    setIsEditingRfid(false)
    handleRefreshRfids()
  }

  function startAddRfid() {
    setRfidIdInput('')
    setRfidNameInput('')
    setRfidEmailInput('')
    setRfidEnabledInput(true)
    setIsAddingRfid(true)
  }

  async function saveAddRfid() {
    await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 54, "rfid_tag": { "id": rfidIdInput, "name": rfidNameInput, "enabled": rfidEnabledInput } })
    })
    setRfidIdInput('')
    setRfidNameInput('')
    setRfidEmailInput('')
    setRfidEnabledInput(true)
    setIsAddingRfid(false)
    handleRefreshRfids()
  }

  async function deleteRfid(rfid: RfidType) {
    await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 26, "id": rfid.id })
    })
    handleRefreshRfids()
  }

  function toggleRfidIsSelectedInRfidCopyModal(chargerId: string) {
    if (chargersToCopyRfidsTo?.some(c => c.chargerId === chargerId)) {
      setChargersToCopyRfidsTo(chargersToCopyRfidsTo?.filter(c => c.chargerId !== chargerId))
    } else {
      const charger = chargers.find(c => c.chargerId === chargerId)
      if (charger) {
        setChargersToCopyRfidsTo(chargersToCopyRfidsTo ? [...chargersToCopyRfidsTo, charger] : [charger])
      }
    }
  }

  async function copyRfidsToSelectedChargers() {
    if (!chargersToCopyRfidsTo || chargersToCopyRfidsTo.length === 0 || !rfids || rfids.length === 0) return

    const rfidList = rfids.map(rfid => ({
      id: rfid.id,
      name: rfid.name,
      enabled: rfid.enabled,
      //email: rfid.email
    }))

    setIsCopyingRfidsInProcess(true)

    const doneCopyingChargerList = []
    for (const charger of chargersToCopyRfidsTo) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ msg_type: 55, rfid_list: rfidList })
      })

      doneCopyingChargerList.push(charger.chargerId)
      setChargersThatRfidsAreCopiedTo(doneCopyingChargerList)
    }
  }

  async function convertExcelFileToObject(file: File) {
    // Only .xlsx files are allowed
    if (file.type == "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
      const xlsxAsArrayMatrix = await readXlsxFile(file)
      // Remove the first row which is the header
      xlsxAsArrayMatrix.shift()
      const xlsxAsRfidObjectArray = xlsxAsArrayMatrix.map(row => ({ id: row[0], name: row[1], enabled: row[2] === "Enabled", email: row[3] } as RfidType))
      setUploadingRfidsList(xlsxAsRfidObjectArray)
    }
  }

  function handleDropFilesToUploadRfidsFromFileModal(e: DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer?.files) { convertExcelFileToObject(Array.from(e.dataTransfer?.files)[0]) }
  }

  function handleAddFileToUploadingFilesWithFilePicker() {
    rfidFileInputRef.current?.click()
  }

  async function saveRfidsFromFileToCharger() {
    if (!uploadingRfidsList || uploadingRfidsList.length === 0) return

    const rfidList = uploadingRfidsList.map(rfid => ({
      id: rfid.id.toString(),
      name: rfid.name.toString(),
      enabled: rfid.enabled,
      // Not writing that in there yet, because in will just set enabled to false if the user does not exist
      //email: rfid.email.toString()
    }))

    await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ msg_type: 55, rfid_list: rfidList })
    })

    setIsUploadingRfidsFromFile(false)
    setUploadingRfidsList(null)
    handleRefreshRfids()
  }

  async function downloadRfidUploadExampleFile() {
    const example = [
      [{ value: 'ID' }, { value: 'Name' }, { value: 'Enabled' }, { value: 'Email' }],
      [{ value: '111111' }, { value: 'Example RFID 1' }, { value: 'Enabled' }, { value: 'one@email.com' }],
      [{ value: '222222' }, { value: 'Example RFID 2' }, { value: 'Disabled' }, { value: 'two@email.com' }],
    ]
    const columns = Array.from({ length: 4 }, () => ({ width: 20 }))
    await writeXlsxFile(example, { columns, fileName: "RFID example.xlsx" })
  }

  function startAddUser() {
    //setIsAddingUserLoading(true)
    setUserEmailInput('')
    setUserNameInput('')
    setUserAccessLevelInput(2)
    setIsAddingUserError(null)
    setIsAddingUser(true)
  }

  async function saveAddUser() {
    setIsAddingUserLoading(true)
    let response
    if (isAdmin) {
      response = await Comm('/api-admin/v2/chargeruser/admin/access/add', {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "chargerId": charger.chargerId, "email": userEmailInput, "name": userNameInput, "userId": charger.userId, "accessLevel": userAccessLevelInput })
      })
    } else {
      response = await Comm('/api-admin/v2/chargeruser/access/add', {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "chargerId": charger.chargerId, "accessLevel": userAccessLevelInput, "email": userEmailInput, "name": userNameInput })
      })
    }
    if (response.ok) {
      setUserEmailInput('')
      setUserNameInput('')
      setUserAccessLevelInput(2)
      setIsAddingUser(false)
      setIsAddingUserError(null)
      handleRefreshUsers()
    } else {
      const message = await readAccessErrorMessage(response)
      if (message) {
        setIsAddingUserError(message)
      } else if (response.status == 404) {
        setIsAddingUserError(t('charger_usersrfids_thisEmailAddressDoesntBelongToAnyRegisteredUser'))
      } else {
        setIsAddingUserError(t('charger_usersrfids_unsuccessfulOperation'))
      }
    }
    setIsAddingUserLoading(false)
  }

  // --- Bulk user invite from xls (VLT-2129), modeled on the RFID file import ---

  function startBulkInviteUsers() {
    setBulkInviteUsersList(null)
    setBulkInviteStatuses({})
    setIsBulkInviteInProcess(false)
    setBulkInviteChargers([charger]) // default to the current charger
    fetchChargers()
    setIsBulkInvitingUsers(true)
  }

  // A bulk invite file's Role cell: an English role name or the level's name in the interface language. Blank
  // means User, but anything else unrecognised is not invited, since falling back to User would give a
  // mistyped Restricted User full access (VLT-3034).
  function roleCellToAccessLevel(role: string): InviteAccessLevel | null {
    const text = role.toLowerCase()
    if (!text) return 2
    return INVITE_ACCESS_LEVELS.find(level => accessLevelToRoleLabel[level].toLowerCase() === text || t(`charger_userAccessLevel_${level}`).toLowerCase() === text) ?? null
  }

  async function convertUsersExcelToObject(file: File) {
    if (file.type == "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
      const matrix = await readXlsxFile(file)
      matrix.shift() // header row (Name, Email, Role)
      const list = matrix.map(row => {
        const role = (row[2] ?? '').toString().trim()
        return { name: (row[0] ?? '').toString(), email: (row[1] ?? '').toString().trim().toLowerCase(), role, accessLevel: roleCellToAccessLevel(role) }
      }).filter(u => u.email)
      setBulkInviteUsersList(list)
    }
  }

  function handleDropFilesToBulkInviteModal(e: DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer?.files) { convertUsersExcelToObject(Array.from(e.dataTransfer?.files)[0]) }
  }

  function handleAddFileToBulkInviteWithFilePicker() {
    inviteFileInputRef.current?.click()
  }

  async function downloadUserInviteExampleFile() {
    const example = [
      [{ value: 'Name' }, { value: 'Email' }, { value: 'Role' }],
      [{ value: 'Example User 1' }, { value: 'one@email.com' }, { value: 'User' }],
      [{ value: 'Example User 2' }, { value: 'two@email.com' }, { value: 'Admin' }],
      [{ value: 'Example User 3' }, { value: 'three@email.com' }, { value: 'Restricted User' }],
    ]
    const columns = [{ width: 30 }, { width: 30 }, { width: 18 }]
    await writeXlsxFile(example, { columns, fileName: "User invite example.xlsx" })
  }

  function toggleChargerInBulkInvite(chargerId: string) {
    if (bulkInviteChargers.some(c => c.chargerId === chargerId)) {
      setBulkInviteChargers(bulkInviteChargers.filter(c => c.chargerId !== chargerId))
    } else {
      const found = chargers.find(c => c.chargerId === chargerId)
      if (found) setBulkInviteChargers([...bulkInviteChargers, found])
    }
  }

  async function runBulkInvite() {
    if (!bulkInviteUsersList || bulkInviteUsersList.length === 0 || bulkInviteChargers.length === 0) return
    setIsBulkInviteInProcess(true)
    const statuses: { [email: string]: BulkInviteStatus } = {}
    for (const invitee of bulkInviteUsersList) {
      if (invitee.accessLevel === null) {
        statuses[invitee.email] = { state: 'error', message: t('charger_usersrfids_unknownRole') }
        setBulkInviteStatuses({ ...statuses })
        continue
      }
      let anyOk = false, any404 = false, anyError = false
      let message: string | null = null
      // Invite this user to each selected charger (silent: per-row status is shown instead of notifications)
      for (const targetCharger of bulkInviteChargers) {
        let response
        if (isAdmin) {
          response = await Comm('/api-admin/v2/chargeruser/admin/access/add', {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ "chargerId": targetCharger.chargerId, "email": invitee.email, "name": invitee.name, "userId": targetCharger.userId, "accessLevel": invitee.accessLevel })
          }, true)
        } else {
          response = await Comm('/api-admin/v2/chargeruser/access/add', {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ "chargerId": targetCharger.chargerId, "accessLevel": invitee.accessLevel, "email": invitee.email, "name": invitee.name })
          }, true)
        }
        if (response.ok) {
          anyOk = true
        } else {
          if (response.status === 404) any404 = true
          else anyError = true
          message = message ?? await readAccessErrorMessage(response)
        }
      }
      const state = (any404 && !anyOk) ? 'unknownEmail' : anyError ? 'error' : 'success'
      statuses[invitee.email] = state === 'success' ? { state } : { state, message: message ?? undefined }
      setBulkInviteStatuses({ ...statuses })
    }
    setIsBulkInviteInProcess(false)
    handleRefreshUsers()
  }

  function bulkInviteStatusLabel(status: BulkInviteStatus) {
    return status.message || t(status.state === 'unknownEmail' ? 'charger_usersrfids_unknownEmail' : 'charger_usersrfids_unsuccessfulOperation')
  }

  function closeBulkInvite() {
    setIsBulkInvitingUsers(false)
    setBulkInviteUsersList(null)
    setBulkInviteChargers([])
    setBulkInviteStatuses({})
    setIsBulkInviteInProcess(false)
  }

  function startChangeRole(user: UserType) {
    setChangingRoleUser(user)
    // Start from the user's current level even when it is not one of the selectable ones: falling back to User
    // here meant that opening and saving the dialog quietly made e.g. a Restricted User a User (VLT-3034).
    setChangeRoleAccessLevelInput(user.access_level)
    setIsChangingRoleError(null)
  }

  // Reuses the same access/add endpoint as adding a user; calling it for an existing email overwrites their access level
  async function saveChangeRole() {
    if (!changingRoleUser) return
    setIsChangingRoleLoading(true)
    let response
    if (isAdmin) {
      response = await Comm('/api-admin/v2/chargeruser/admin/access/add', {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "chargerId": charger.chargerId, "email": changingRoleUser.email, "name": changingRoleUser.name, "userId": charger.userId, "accessLevel": changeRoleAccessLevelInput })
      })
    } else {
      response = await Comm('/api-admin/v2/chargeruser/access/add', {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "chargerId": charger.chargerId, "accessLevel": changeRoleAccessLevelInput, "email": changingRoleUser.email, "name": changingRoleUser.name })
      })
    }
    if (response.ok) {
      setChangingRoleUser(null)
      setIsChangingRoleError(null)
      addNotification(t('charger_usersrfids_roleUpdated'), "TEXT")
      handleRefreshUsers()
    } else {
      setIsChangingRoleError(await readAccessErrorMessage(response) ?? t('charger_usersrfids_unsuccessfulOperation'))
    }
    setIsChangingRoleLoading(false)
  }

  async function deleteUser(user: UserType) {
    if (!(await confirm({ message: `${t('charger_usersrfids_areYouSureYouWantToRemoveThisUser')} (${user.email})`, showCancel: true }))) return
    let response
    if (isAdmin) {
      response = await Comm('/api-admin/v2/chargeruser/admin/access/delete', {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "chargerId": charger.chargerId, "email": user.email, "userId": charger.userId })
      })
    } else {
      response = await Comm('/api-admin/v2/chargeruser/access/delete', {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "chargerId": charger.chargerId, "email": user.email })
      })
    }
    if (!response.ok) {
      addNotification(await readAccessErrorMessage(response) ?? t('charger_usersrfids_unsuccessfulOperation'), "TEXT", "error")
    }
    handleRefreshUsers()
  }

  async function handleTransferOwnership() {
    setIsTransferingOwnershipLoading(true)
    let response
    if (isAdmin) {
      response = await Comm('/api-admin/v2/chargeruser/admin/access/transfer', {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "chargerId": charger.chargerId, "email": transferOwenrshipEmailInput, "userId": charger.userId })
      })
    } else {
      response = await Comm('/api-admin/v2/chargeruser/access/transfer', {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "chargerId": charger.chargerId, "email": transferOwenrshipEmailInput })
      })
    }

    if (response.ok) {
      setIsTransferingOwnership(false)
      setTransferOwenrshipEmailInput('')
      setIsTransferingOwnershipError(null)
      navigate('/chargers')
    } else {
      setIsTransferingOwnershipError(await readAccessErrorMessage(response) ?? t('charger_usersrfids_errorWhileTransferringOwnership'))
    }
    setIsTransferingOwnershipLoading(false)
  }

  async function handleRemoveAllRFIDs() {
    if (await confirm({ message: t('charger_usersrfids_areYouSureThisWillRemoveAllRfidsFromThisCharger'), showCancel: true })) {

      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "eof": true, "first_item": 0, "msg_type": 96, "rfid_list": [] })
      })
      handleRefreshRfids()
    }
  }

  // Permissions report (VLT-2130): one row per user with role and the chargers they're assigned to.
  // The assigned chargers come from each user's Firebase node, so this is fetched on demand at click time.
  async function exportUserPermissionsReport() {
    if (!users) return
    const dbRef = ref(getDatabase(fbConf))

    const rows: { value: string | number, fontWeight?: string, wrap?: boolean }[][] = []
    rows.push(['Name', 'Email', 'Access level', 'Chargers'].map(label => ({ value: label, fontWeight: 'bold' })))

    for (const user of users) {
      let chargersString = '-'
      try {
        const response = await get(child(dbRef, `voltie/chargerusers/${user.user_id}/chargers`))
        const chargersVal = response.val()
        if (chargersVal) {
          chargersString = Object.keys(chargersVal).map(id => chargersVal[id]?.name ? `${chargersVal[id].name} (${id})` : id).join('\n')
        }
      } catch { /* leave as - */ }
      rows.push([
        { value: user.name || '-' },
        { value: user.email || '-' },
        { value: t(`charger_userAccessLevel_${user.access_level}`) },
        { value: chargersString, wrap: true }
      ])
    }

    const columns = [{ width: 24 }, { width: 32 }, { width: 18 }, { width: 60 }]
    // @ts-expect-error rows shape matches the lib's SheetData
    await writeXlsxFile(rows, { columns, fileName: `Users permissions ${new Date().toLocaleString('hu')}.xlsx` })
  }

  return (
    <>
      <input ref={rfidFileInputRef} type="file" accept=".xls,.xlsx" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) convertExcelFileToObject(f); e.target.value = '' }} />
      <input ref={inviteFileInputRef} type="file" accept=".xls,.xlsx" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) convertUsersExcelToObject(f); e.target.value = '' }} />
      {isEditingRfid && <>
        <Modal onEscape={() => setIsEditingRfid(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsEditingRfid(false)} />
            </ModalCloseButtonContainer>
            <Icon path="chargers.rfidTag" width="96px" height="96px" marginleft="calc((100% - 96px) / 2)" />
            <WhiteSpace height="30px" />
            <InputWithLabel label={t('charger_usersrfids_rfidId')} width={"max(16vw, 204.8px)"} value={rfidIdInput || ''} onInput={() => { }} />
            <WhiteSpace height="8px" />
            <InputWithLabel label={t('charger_usersrfids_rfidName')} width={"max(16vw, 204.8px)"} value={rfidNameInput || ''} onInput={e => setRfidNameInput(e.replace(/[:\\\/\?\*\[\]]/g, "").slice(0, 30))} />
            <WhiteSpace height="8px" />
            <Select width={"100%"} options={users ? [rfidEmailInput || '', ...users.map((user) => user.email)] : []} handleSelect={e => setRfidEmailInput(e)} isAbsolute />
            <WhiteSpace height="12px" />
            <div style={{ display: 'flex' }}>
              <SwitchLabel>{`${t('charger_enabled')}:`}</SwitchLabel>
              <div style={{ position: 'absolute', right: '88px' }}>
                <Switch toggled={rfidEnabledInput || false} setToggled={setRfidEnabledInput} />
              </div>
            </div>
            <WhiteSpace height="12px" />
            <div style={{ display: 'flex' }}>
              <Button onclick={() => setIsEditingRfid(false)} label={t('charger_cancel')} width='calc(max(8vw, 102.4px) - 8px)' bgcolor="transparent" labelcolor="black" />
              <WhiteSpace width="8px" />
              <Button onclick={saveEditRfid} label={t('charger_save')} width='max(8vw, 102.4px)' />
            </div>
          </ModalContainer>
        </Modal>
      </>}

      {isAddingRfid && <>
        <Modal onEscape={() => setIsAddingRfid(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsAddingRfid(false)} />
            </ModalCloseButtonContainer>
            <Icon path="chargers.rfidTag" width="96px" height="96px" marginleft="calc((100% - 96px) / 2)" />
            <WhiteSpace height="30px" />
            <InputWithLabel label={t('charger_usersrfids_rfidId')} width={"max(16vw, 204.8px)"} value={rfidIdInput || ''} onInput={e => setRfidIdInput(e.replace(/[:\\\/\?\*\[\]]/g, "").slice(0, 30))} />
            <WhiteSpace height="8px" />
            <InputWithLabel label={t('charger_usersrfids_rfidName')} width={"max(16vw, 204.8px)"} value={rfidNameInput || ''} onInput={e => setRfidNameInput(e.replace(/[:\\\/\?\*\[\]]/g, "").slice(0, 30))} />
            <WhiteSpace height="12px" />
            <div style={{ display: 'flex' }}>
              <SwitchLabel>{`${t('charger_enabled')}:`}</SwitchLabel>
              <div style={{ position: 'absolute', right: '88px' }}>
                <Switch toggled={rfidEnabledInput || false} setToggled={setRfidEnabledInput} />
              </div>
            </div>
            <WhiteSpace height="12px" />
            <div style={{ display: 'flex' }}>
              <Button onclick={() => setIsAddingRfid(false)} label={t('charger_cancel')} width='calc(max(8vw, 102.4px) - 8px)' bgcolor="transparent" labelcolor="black" />
              <WhiteSpace width="8px" />
              <Button onclick={saveAddRfid} label={t('charger_save')} width='max(8vw, 102.4px)' />
            </div>
          </ModalContainer>
        </Modal>
      </>}

      {isCopyingRfid && <>
        <Modal align="right" onEscape={() => {/* The close process is more complicated */ }}>
          <div style={{ height: '85dvh', padding: '24px' }}>
            {!isCopyingRfidsInProcess && <>
              <InputWithIcon onInput={(input) => setChargersToShowAfterSearch(chargers?.filter(charger => (normalizeString(`${charger.chargerId} ${charger.localName}`.toLocaleLowerCase())).includes(normalizeString(input.toLocaleLowerCase()))))} width="max(20vw, 256px)" placeholder={t('chargers_search')} icon="general.search" iconColor="gray_600" />
              <WhiteSpace height="12px" />
            </>}
            {!chargersToShowAfterSearch && <LoaderDots />}
            <div style={{ overflowY: 'scroll', height: 'calc(85dvh - 90px)' }}>

              {!isCopyingRfidsInProcess ? <>

                {chargersToShowAfterSearch && chargersToShowAfterSearch.map(charger => chargersToCopyRfidsTo?.includes(charger) && (
                  <div key={charger.chargerId} style={{ display: 'flex', cursor: 'pointer' }} onClick={() => toggleRfidIsSelectedInRfidCopyModal(charger.chargerId)}>
                    <Icon width="16px" height="16px" margintop="11px" path='general.checkboxFull' />
                    <WhiteSpace width="12px" />
                    <div style={{ display: 'flex' }}>
                      <CopyRfidsModalChargerLabel style={{ width: '140px' }}>{charger.chargerId}</CopyRfidsModalChargerLabel>
                      <CopyRfidsModalChargerLabel>{charger.localName}</CopyRfidsModalChargerLabel>
                    </div>
                  </div>
                ))}

                {chargersToShowAfterSearch && chargersToShowAfterSearch.map(charger => !chargersToCopyRfidsTo?.includes(charger) && (
                  <div key={charger.chargerId} style={charger.online ? { display: 'flex', cursor: 'pointer' } : { display: 'flex', cursor: 'default' }} onClick={() => charger.online && toggleRfidIsSelectedInRfidCopyModal(charger.chargerId)}>
                    <Icon width="16px" height="16px" margintop="11px" path='general.checkboxEmpty' />
                    <WhiteSpace width="12px" />
                    <div style={{ display: 'flex' }}>
                      <CopyRfidsModalChargerLabel $isOffline={!charger.online} style={{ width: '140px' }}>{charger.chargerId}</CopyRfidsModalChargerLabel>
                      <CopyRfidsModalChargerLabel $isOffline={!charger.online}>{charger.localName}</CopyRfidsModalChargerLabel>
                    </div>
                    {!charger.online && <>
                      <WhiteSpace width="12px" />
                      <p style={{ backgroundColor: '#f0f0f0', padding: '1px 6px', borderRadius: '4px', color: '#888', fontSize: '12px' }}>{t('charger_offline')}</p>
                    </>}
                  </div>
                ))}
              </> : <>

                {chargersToCopyRfidsTo && chargersToCopyRfidsTo.map(charger => (
                  <div key={charger.chargerId} style={{ display: 'flex' }}>
                    {chargersThatRfidsAreCopiedTo?.includes(charger.chargerId) ? <>
                      <Icon width="16px" height="16px" margintop="11px" path='general.check' color="green" />
                      <WhiteSpace width="8px" />
                      <p style={{ backgroundColor: '#f0f0f0', padding: '1px 6px', borderRadius: '4px', color: '#888', fontSize: '12px' }}>{t('charger_success')}</p>
                    </> : <>
                      <WhiteSpace width="16px" />
                    </>}
                    <WhiteSpace width="12px" />
                    <div style={{ display: 'flex' }}>
                      <CopyRfidsModalChargerLabel style={{ width: '140px' }}>{charger.chargerId}</CopyRfidsModalChargerLabel>
                      <CopyRfidsModalChargerLabel>{charger.localName}</CopyRfidsModalChargerLabel>
                    </div>
                  </div>
                ))}
              </>}
            </div>
            <ModalButtonContainer>
              {!isCopyingRfidsInProcess && (
                <ModalButton onClick={copyRfidsToSelectedChargers} $hasBg>{t('charger_copy')}</ModalButton>
              )}
              <ModalButton onClick={() => { setIsCopyingRfid(false); setChargersToCopyRfidsTo([]); setChargersThatRfidsAreCopiedTo([]); setIsCopyingRfidsInProcess(false); setChargersToShowAfterSearch(chargers) }}>{isCopyingRfidsInProcess ? t('charger_close') : t('charger_cancel')}</ModalButton>
            </ModalButtonContainer>
          </div>
        </Modal>
      </>}

      {isUploadingRfidsFromFile && <>
        <Modal onEscape={() => setIsUploadingRfidsFromFile(false)}>
          <div style={{ width: 'min(max(60vw, 768px), calc(100vw - 72px))', padding: '24px', paddingTop: uploadingRfidsList ? '12px' : '24px' }}>
            {!uploadingRfidsList && <>
              <ModalCloseButtonContainer>
                <Icon path="general.x" width="20px" height="20px" onClick={() => setIsUploadingRfidsFromFile(false)} />
              </ModalCloseButtonContainer>
              <WhiteSpace height="32px" />
              <FileDropContainer onDrop={(e) => handleDropFilesToUploadRfidsFromFileModal(e as unknown as DragEvent)} onDragOver={(e) => e.preventDefault()} onClick={handleAddFileToUploadingFilesWithFilePicker}>
                <WhiteSpace height="16px" />
                <Icon path="chargerGroups.fileDropUpload" width="48px" height="48px" marginleft="calc((100% - 48px) / 2)" />
                <WhiteSpace height="8px" />
                <FileDropLabel>{t('charger_usersrfids_dropXlsxFileOfClickToBrowse')}</FileDropLabel>
                <WhiteSpace height="16px" />
              </FileDropContainer>
              <WhiteSpace height="16px" />
              <div style={{ display: 'flex', justifyContent: 'right' }}>
                <ButtonWithIcon icon="general.download" label={t('charger_usersrfids_downloadExampleFile')} onClick={downloadRfidUploadExampleFile} backgroundColor="dark" iconColor="white" />
              </div>
            </>}

            {uploadingRfidsList && uploadingRfidsList.length > 0 && <>
              <Table id="UsersRfids_RfidUploadFromFile" height="calc(85dvh - 126px)" columns={[
                { width: '30px' },
                { label: 'NO', width: '50px' },
                { label: t('charger_rfidNumber'), width: '18%' },
                { label: t('charger_rfidName'), width: '24%' },
                { label: t('charger_active'), width: '15%' },
                { label: t('charger_user') },
              ]} data={uploadingRfidsList.map((rfid, index) => [
                {
                  value: {
                    type: TableCellTypes.Image,
                    src: 'chargers.rfidTag'
                  }
                }, {
                  sortBy: index,
                  value: {
                    type: TableCellTypes.Label,
                    value: index + 1 + '.'
                  }
                }, {
                  sortBy: rfid.id,
                  value: {
                    type: TableCellTypes.Label,
                    value: rfid.id || '-'
                  }
                }, {
                  sortBy: rfid.name,
                  value: {
                    type: TableCellTypes.Label,
                    value: rfid.name || '-'
                  }
                }, {
                  sortBy: rfid.enabled ? 'a' : 'b',
                  value: {
                    type: TableCellTypes.Status,
                    label: rfid.enabled ? t('charger_active') : t('charger_inactive'),
                    themeColor: rfid.enabled ? 'success' : 'gray_400'
                  }
                }, {
                  sortBy: rfid.email || '',
                  value: {
                    type: TableCellTypes.Label,
                    value: rfid.email || '-'
                  }
                }
              ])} />
            </>}

            {uploadingRfidsList && <>
              <WhiteSpace height="38px" />
              <ModalButtonContainer>
                <ModalButton onClick={saveRfidsFromFileToCharger} $hasBg>{t('charger_save')}</ModalButton>
                <ModalButton onClick={() => { setIsUploadingRfidsFromFile(false); setUploadingRfidsList(null) }}>{t('charger_cancel')}</ModalButton>
              </ModalButtonContainer>
            </>}
          </div>
        </Modal>
      </>}

      {isBulkInvitingUsers && <>
        <Modal onEscape={closeBulkInvite}>
          <div style={{ width: 'min(max(60vw, 768px), calc(100vw - 72px))', padding: '24px', maxHeight: '85dvh', overflowY: 'scroll', position: 'relative' }}>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={closeBulkInvite} />
            </ModalCloseButtonContainer>
            <p style={{ fontWeight: 700, fontSize: '18px', margin: 0, marginBottom: '16px' }}>{t('charger_usersrfids_bulkInviteUsers')}</p>

            {!bulkInviteUsersList ? <>
              <FileDropContainer onDrop={(e) => handleDropFilesToBulkInviteModal(e as unknown as DragEvent)} onDragOver={(e) => e.preventDefault()} onClick={handleAddFileToBulkInviteWithFilePicker}>
                <WhiteSpace height="16px" />
                <Icon path="chargerGroups.fileDropUpload" width="48px" height="48px" marginleft="calc((100% - 48px) / 2)" />
                <WhiteSpace height="8px" />
                <FileDropLabel style={{ width: 'auto' }}>{t('charger_usersrfids_dropXlsxFileOfClickToBrowse')}</FileDropLabel>
                <WhiteSpace height="16px" />
              </FileDropContainer>
              <WhiteSpace height="16px" />
              <p style={{ fontSize: '12px', color: '#A1A5B7', margin: 0 }}>{t('charger_usersrfids_fileColumns')}: Name, Email, Role</p>
              <p style={{ fontSize: '12px', color: '#A1A5B7', margin: 0, marginTop: '4px' }}>{t('charger_usersrfids_acceptedRoles')}: {ROLE_SELECT_OPTIONS.join(', ')}</p>
              <WhiteSpace height="12px" />
              <div style={{ display: 'flex', justifyContent: 'right' }}>
                <ButtonWithIcon icon="general.download" label={t('charger_usersrfids_downloadExampleFile')} onClick={downloadUserInviteExampleFile} backgroundColor="dark" iconColor="white" />
              </div>
            </> : <>
              <SwitchLabel>{t('charger_usersrfids_selectChargers')}</SwitchLabel>
              <WhiteSpace height="4px" />
              <InputWithIcon onInput={(input) => setChargersToShowAfterSearch(chargers?.filter(c => normalizeString(`${c.chargerId} ${c.localName}`.toLocaleLowerCase()).includes(normalizeString(input.toLocaleLowerCase()))))} width="100%" placeholder={t('chargers_search')} icon="general.search" iconColor="gray_600" />
              <WhiteSpace height="8px" />
              <div style={{ maxHeight: '20dvh', overflowY: 'scroll', border: '1px solid #E4E6EF', borderRadius: '8px', padding: '8px' }}>
                {(chargersToShowAfterSearch || chargers).map(c => (
                  <div key={c.chargerId} style={{ display: 'flex', cursor: 'pointer', alignItems: 'center', marginBottom: '4px' }} onClick={() => toggleChargerInBulkInvite(c.chargerId)}>
                    <Icon width="16px" height="16px" path={bulkInviteChargers.some(bc => bc.chargerId === c.chargerId) ? 'general.checkboxFull' : 'general.checkboxEmpty'} />
                    <WhiteSpace width="8px" />
                    <CopyRfidsModalChargerLabel style={{ width: '140px' }}>{c.chargerId}</CopyRfidsModalChargerLabel>
                    <CopyRfidsModalChargerLabel>{c.localName}</CopyRfidsModalChargerLabel>
                  </div>
                ))}
              </div>
              <WhiteSpace height="16px" />

              {bulkInviteUsersList.map((u, i) => (
                <div key={`${u.email}${i}`} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                  <CopyRfidsModalChargerLabel style={{ width: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || '-'}</CopyRfidsModalChargerLabel>
                  <CopyRfidsModalChargerLabel style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</CopyRfidsModalChargerLabel>
                  <WhiteSpace width="8px" />
                  <CopyRfidsModalChargerLabel style={{ width: '110px', ...(u.accessLevel === null && { color: '#FD4E4E' }) }} title={u.accessLevel === null ? t('charger_usersrfids_unknownRole') : undefined}>
                    {u.accessLevel === null ? u.role : accessLevelToRoleLabel[u.accessLevel]}
                  </CopyRfidsModalChargerLabel>
                  <WhiteSpace width="8px" />
                  {bulkInviteStatuses[u.email]?.state === 'success' && <p style={{ backgroundColor: '#f0f0f0', padding: '1px 6px', borderRadius: '4px', color: 'green', fontSize: '12px', margin: 0 }}>{t('charger_success')}</p>}
                  {bulkInviteStatuses[u.email] && bulkInviteStatuses[u.email].state !== 'success' && <p style={{ backgroundColor: '#f0f0f0', padding: '1px 6px', borderRadius: '4px', color: '#FD4E4E', fontSize: '12px', margin: 0 }}>{bulkInviteStatusLabel(bulkInviteStatuses[u.email])}</p>}
                </div>
              ))}

              <WhiteSpace height="16px" />
              <div style={{ display: 'flex' }}>
                <Button onclick={closeBulkInvite} label={t('charger_close')} width='calc(50% - 8px)' bgcolor="transparent" labelcolor="black" />
                <WhiteSpace width="8px" />
                <Button onclick={isBulkInviteInProcess ? () => { } : runBulkInvite} label={isBulkInviteInProcess ? <LoaderDots /> : t('charger_usersrfids_invite')} width='50%' bgcolor="#4AB1FC" labelcolor="white" />
              </div>
            </>}
          </div>
        </Modal>
      </>}

      {isAddingUser && <>
        <Modal onEscape={() => setIsAddingUser(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsAddingUser(false)} />
            </ModalCloseButtonContainer>
            <Icon path="chargers.user" width="96px" height="96px" marginleft="calc((100% - 96px) / 2)" />
            <WhiteSpace height="30px" />
            <InputWithLabel label={t('charger_usersrfids_userEmail')} width={"max(16vw, 204.8px)"} value={userEmailInput || ''} onInput={e => setUserEmailInput(e)} />
            <WhiteSpace height="8px" />
            <InputWithLabel label={t('charger_usersrfids_userName')} width={"max(16vw, 204.8px)"} value={userNameInput || ''} onInput={e => setUserNameInput(e.replace(/[:\\\/\?\*\[\]]/g, "").slice(0, 30))} />
            <WhiteSpace height="8px" />
            <Select width={"100%"} options={ROLE_SELECT_OPTIONS} handleSelect={(e) => setUserAccessLevelInput(roleLabelToAccessLevel(e))} isAbsolute />
            <WhiteSpace height="24px" />
            {isAddingUserError && <>
              <p style={{ fontSize: '12px', color: "#FD4E4E", width: '100%', textAlign: 'center' }}>{isAddingUserError}</p>
            </>}
            <div style={{ display: 'flex' }}>
              <Button onclick={() => setIsAddingUser(false)} label={t('charger_cancel')} width='calc(max(8vw, 102.4px) - 8px)' bgcolor="transparent" labelcolor="black" />
              <WhiteSpace width="8px" />
              <Button onclick={saveAddUser} label={isAddingUserLoading ? <LoaderDots /> : t('charger_save')} width='max(8vw, 102.4px)' />
            </div>
          </ModalContainer>
        </Modal>
      </>}

      {changingRoleUser && <>
        <Modal onEscape={() => setChangingRoleUser(null)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setChangingRoleUser(null)} />
            </ModalCloseButtonContainer>
            <Icon path="chargers.user" width="96px" height="96px" marginleft="calc((100% - 96px) / 2)" />
            <WhiteSpace height="30px" />
            <InputWithLabel label={t('charger_usersrfids_userEmail')} width={"max(16vw, 204.8px)"} value={changingRoleUser.email || ''} onInput={() => { }} disabled />
            <WhiteSpace height="8px" />
            <Select width={"100%"} options={[accessLevelToRoleLabel[changingRoleUser.access_level] || t(`charger_userAccessLevel_${changingRoleUser.access_level}`), ...ROLE_SELECT_OPTIONS]} handleSelect={(e) => setChangeRoleAccessLevelInput(ROLE_SELECT_OPTIONS.includes(e) ? roleLabelToAccessLevel(e) : changingRoleUser.access_level)} isAbsolute />
            <WhiteSpace height="24px" />
            {isChangingRoleError && <>
              <p style={{ fontSize: '12px', color: "#FD4E4E", width: '100%', textAlign: 'center' }}>{isChangingRoleError}</p>
            </>}
            <div style={{ display: 'flex' }}>
              <Button onclick={() => setChangingRoleUser(null)} label={t('charger_cancel')} width='calc(max(8vw, 102.4px) - 8px)' bgcolor="transparent" labelcolor="black" />
              <WhiteSpace width="8px" />
              <Button onclick={saveChangeRole} label={isChangingRoleLoading ? <LoaderDots /> : t('charger_save')} width='max(8vw, 102.4px)' />
            </div>
          </ModalContainer>
        </Modal>
      </>}

      {isTransferingOwnership && <>
        <Modal onEscape={() => { setIsTransferingOwnership(false); setTransferOwenrshipEmailInput(''); setIsTransferingOwnershipError(null) }}>
          <div style={{ padding: '28px' }}>
            <InputWithLabel label={t('charger_usersrfids_userEmail')} width={"max(16vw, 204.8px)"} value={transferOwenrshipEmailInput || ''} onInput={(e) => setTransferOwenrshipEmailInput(e)} />
            {isTransferingOwnershipError ? <>
              <p style={{ fontSize: '12px', color: "#FD4E4E", width: '100%', textAlign: 'center' }}>{isTransferingOwnershipError}</p>
            </> : <>
              <WhiteSpace height="12px" />
            </>}
            <div style={{ display: 'flex' }}>
              <Button onclick={() => { setIsTransferingOwnership(false); setTransferOwenrshipEmailInput(''); setIsTransferingOwnershipError(null) }} label={t('charger_cancel')} width='calc(max(8vw, 102.4px) - 8px)' bgcolor="transparent" labelcolor="black" />
              <WhiteSpace width="8px" />
              <Button onclick={handleTransferOwnership} label={isTransferingOwnershipLoading ? <LoaderDots /> : t('charger_usersrfids_transfer')} width='max(8vw, 102.4px)' />
            </div>
          </div>
        </Modal>
      </>}


      <WhiteSpace height={'12px'} />
      <PageTitle label={t('charger_rfidManagement')} />
      <ToolbarContaier>
        <div>
          <ButtonWithIcon icon="general.plus" label={t('charger_usersrfids_addNewRfid')} onClick={startAddRfid} iconColor="white" backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.upload" label={t('charger_usersrfids_uploadRfidsFromFile')} onClick={() => setIsUploadingRfidsFromFile(true)} iconColor="white" backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.export" label={t('charger_usersrfids_copyRfidsToChargers')} onClick={() => { fetchChargers(); setIsCopyingRfid(true) }} iconColor="white" backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.trash" label={t('charger_usersrfids_removeAllRfids')} onClick={handleRemoveAllRFIDs} iconColor="white" backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.refresh" label={t('logs_refresh')} onClick={handleRefreshRfids} backgroundColor="success" iconColor="white" />
          <RefreshTimerLabel>{lastRfidsRefresh}</RefreshTimerLabel>
        </div>
        <WhiteSpace width="8px" />
        <div>
          {rfids && <ExportButton fileName="RFIDs" titleRow={[
            { label: 'ID', width: '40' },
            { label: 'Name', width: '35' },
            { label: 'Enabled', width: '20' },
            { label: 'Email', width: '40' },
            { label: 'Last Use', width: '20' }
          ]} data={rfids.map(rfid => [rfid.id, rfid.name, rfid.enabled ? 'Enabled' : 'Disabled', rfid.email, rfid.last_use ? new Date(rfid.last_use * 1000).toLocaleString('hu') : ''])} />
          }
        </div>
      </ToolbarContaier>
      {rfids && rfids.length > 0 ? <>
        <Table id="UsersRfids_Rfids" height="calc((90vh - 400px) / 2)" isSearchEnabled={true} isPaginationEnabled={true} columns={[
          { width: '30px' },
          { label: t('charger_rfidName'), width: '20%' },
          { label: t('charger_rfidState'), width: '20%' },
          { label: t('charger_rfidNumber'), width: '20%' },
          { label: t('charger_lastUse'), width: '20%' },
          { label: t('charger_user') },
          { width: '20px' }
        ]} data={rfids.map(rfid => [
          {
            value: {
              type: TableCellTypes.Image,
              src: 'chargers.rfidTag'
            }
          }, {
            sortBy: rfid.name,
            value: {
              type: TableCellTypes.Label,
              value: rfid.name || '-'
            }
          }, {
            sortBy: rfid.enabled ? 'a' : 'b',
            value: {
              type: TableCellTypes.Status,
              label: rfid.enabled ? t('charger_active') : t('charger_inactive'),
              themeColor: rfid.enabled ? 'success' : 'gray_400'
            }
          }, {
            sortBy: rfid.id,
            value: {
              type: TableCellTypes.Label,
              value: rfid.id || '-'
            }
          }, {
            sortBy: rfid.last_use || 0,
            value: {
              type: TableCellTypes.LabelDivided,
              value1: rfid.last_use ? new Date(rfid.last_use * 1000).toLocaleDateString('hu') : '-',
              value2: rfid.last_use ? new Date(rfid.last_use * 1000).toLocaleTimeString('hu') : ''
            }
          }, {
            sortBy: rfid.email || '',
            value: {
              type: TableCellTypes.Label,
              value: rfid.email || '-'
            }
          }, {
            custom: <Dropdown handleChoose={(value) => value == t('charger_edit') ? startEditRfid(rfid) : deleteRfid(rfid)} title={{ icon: "general.more", label: '' }} options={[{ icon: 'general.wrench', label: t('charger_edit') }, { icon: "general.trash", label: t('charger_delete'), color: '#FD4E4E' }]} showOptionContainerOnLeft />
          }
        ])} />
      </> : rfids && rfids.length == 0 ? <>
        <Table id="UsersRfids_Rfids" height="calc((90vh - 400px) / 2)" isSearchEnabled={true} isRenderEmpty={true} columns={[
          { width: '30px' },
          { label: t('charger_rfidName'), width: '20%' },
          { label: t('charger_rfidState'), width: '20%' },
          { label: t('charger_rfidNumber'), width: '20%' },
          { label: t('charger_lastUse'), width: '20%' },
          { label: t('charger_user') },
          { width: '20px' }
        ]} data={[[{}, {}, {}, {}, {}, {}, {}]]} />
      </> : <>
        <EmptyTableLabel><LoaderDots /></EmptyTableLabel>
        <WhiteSpace height="calc((90vh - 124px) / 2)" />
      </>}









      <PageTitle label={t('charger_users')} />
      <ToolbarContaier>
        <div>
          <ButtonWithIcon icon="general.forwardArrow" label={t('charger_usersrfids_transferOwnership')} onClick={() => setIsTransferingOwnership(true)} iconColor="white" backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.plus" label={t('charger_usersrfids_addNewUser')} onClick={startAddUser} iconColor="white" backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.upload" label={t('charger_usersrfids_bulkInviteUsers')} onClick={startBulkInviteUsers} iconColor="white" backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.refresh" label={t('logs_refresh')} onClick={handleRefreshUsers} backgroundColor="success" iconColor="white" />
          <RefreshTimerLabel>{lastUsersRefresh}</RefreshTimerLabel>
        </div>
        <WhiteSpace width="8px" />
        <div>
          {users && <ButtonWithIcon icon="general.export" label={t('chargerLogs_export')} onClick={exportUserPermissionsReport} backgroundColor="white" />}
        </div>
      </ToolbarContaier>
      {users && users.length > 0 ? <>
        <Table id="UsersRfids_Users" height="calc((90vh - 400px) / 2)" isSearchEnabled={true} isPaginationEnabled={true} columns={[
          { width: '30px' },
          { label: t('charger_userEmail'), width: '20%' },
          { label: t('charger_nameUserUid'), width: '20%' },
          { label: t('charger_permissionLevel'), width: '20%' },
          { label: t('charger_created'), width: '20%' },
          { label: t('charger_signedIn') },
          { width: '20px' }
        ]} data={users.map(user => [
          {
            value: {
              type: TableCellTypes.Image,
              src: 'chargers.user'
            }
          }, {
            onClick: isAdmin ? () => window.open(`https://console.firebase.google.com/project/voltie-charger/database/voltie-charger-default-rtdb/data/~2Fvoltie~2Fchargerusers~2F${user.user_id}`, '_blank') : null as unknown as () => unknown,
            sortBy: user.email,
            value: {
              type: TableCellTypes.Label,
              value: user.email || '-'
            }
          }, {
            onClick: isAdmin ? () => window.open(`https://console.firebase.google.com/project/voltie-charger/database/voltie-charger-default-rtdb/data/~2Fvoltie~2Fchargerusers~2F${user.user_id}`, '_blank') : null as unknown as () => unknown,
            sortBy: user.name,
            value: {
              type: TableCellTypes.LabelDivided,
              value1: user.name,
              value2: user.user_id
            }
          }, {
            sortBy: user.access_level,
            value: {
              type: TableCellTypes.Status,
              label: t(`charger_userAccessLevel_${user.access_level}`),
              themeColor: (() => {
                switch (user.access_level) {
                  case 0: return 'gray_400'
                  case 1: return 'gray_400'
                  case 2: return 'primary'
                  case 3: return 'gray_900'
                  case 4: return 'gray_400'
                  case 5: return 'gray_400'
                  case 6: return 'gray_400'
                  case 7: return 'success'
                  case 9: return 'success'
                  case 10: return 'success'
                  default: return 'success'
                }
              })()
            }
          }, {
            value: {
              type: TableCellTypes.Label,
              value: '-'
            }
          }, {
            value: {
              type: TableCellTypes.Label,
              value: '-'
            }
          }, {
            custom: <Dropdown handleChoose={(value) => {
              if (value == t('charger_usersrfids_changeRole')) startChangeRole(user)
              else if (value == t('charger_delete')) deleteUser(user)
            }} title={{ icon: "general.more", label: '' }} options={[{ icon: 'general.wrench', label: t('charger_usersrfids_changeRole') }, { icon: "general.trash", label: t('charger_delete'), color: '#FD4E4E' }]} showOptionContainerOnLeft />
          }
        ])} />
      </> : users && users.length == 0 ? <>
        <Table id="UsersRfids_Rfids" height="calc((90vh - 400px) / 2)" isSearchEnabled={true} isRenderEmpty={true} columns={[
          { width: '30px' },
          { label: t('charger_userEmail'), width: '20%' },
          { label: t('charger_nameUserUid'), width: '20%' },
          { label: t('charger_permissionLevel'), width: '20%' },
          { label: t('charger_created'), width: '20%' },
          { label: t('charger_signedIn') },
          { width: '20px' }
        ]} data={[[{}, {}, {}, {}, {}, {}, {}]]} />
      </> : <>
        <EmptyTableLabel><LoaderDots /></EmptyTableLabel>
        <WhiteSpace height="calc((90vh - 124px) / 2)" />
      </>}
    </>
  )
}